import React, { useEffect, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useParams, useLocation } from 'react-router-dom';
import { fetchCardDetails, fetchCardInventory, fetchCardInventoryByID, fetchAverageBatter } from '../../api/cards';
import Card from '../../components/Card';
import CardBacksPicker from '../../components/CardBacksPicker';
import CardUpgrades from '../../components/CardUpgrades';
import { combineBattingRecords, combinePitchingRecords, formatAverage } from '../../utils/statsUtils';
import { rollDiceForCard, rollDiceForCardVs, calculateBattingStats, calculatePitchingStats as calcPitchingSimStats } from '../../utils/statsUtils';
import { teamNameTranslator } from '../../helpers/teamHelpers';
import GLOBALS from '../../config/globals';
import { getEffectiveCardStatsFromUpgrades, getUpgradeLevel, getUpgradeOptionsForCardType, parseCardUpgrades } from '../../utils/cardUpgrades';
import { useAtom } from 'jotai';
import { currentUserAtom } from '../../auth/AuthAtoms';
import LoadingSpinner from '../../components/LoadingSpinner';
import BackButton from '../../components/BackButton';
import CardPreviewModal from '../../components/CardPreviewModal';

const StatRow = ({ label, real, game, sim, showSim, highlight }) => (
    <tr className={highlight ? 'stat-row-highlight' : ''}>
        <td className="stat-label">{label}</td>
        <td className="stat-value">{real ?? '—'}</td>
        <td className="stat-value">{game ?? '—'}</td>
        {showSim && <td className="stat-value">{sim ?? '—'}</td>}
    </tr>
);

StatRow.propTypes = {
    label: PropTypes.string.isRequired,
    real: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    game: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    sim: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    showSim: PropTypes.bool,
    highlight: PropTypes.bool,
};

const StatSection = ({ title, colSpan, children }) => (
    <>
        <tr className="stat-section-header">
            <td colSpan={colSpan}>{title}</td>
        </tr>
        {children}
    </>
);

StatSection.propTypes = {
    title: PropTypes.string.isRequired,
    colSpan: PropTypes.number.isRequired,
    children: PropTypes.node.isRequired,
};

const CardDetailsPage = () => {
    const [currentUser] = useAtom(currentUserAtom);
    const { cardID, inventoryID } = useParams();
    const location = useLocation();
    const [card, setCard] = useState(location.state?.card || null);
    const [cardInventory, setCardInventory] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [variants, setVariants] = useState([]);
    const [numRolls, setNumRolls] = useState(100);
    const [simStats, setSimStats] = useState(null);
    const [pitcherSimStats, setPitcherSimStats] = useState(null);
    const [averageBatter, setAverageBatter] = useState(null);
    const [showBatterPreview, setShowBatterPreview] = useState(false);

    const isAdmin = currentUser && currentUser.isAdmin;
    const canViewDice = isAdmin || ['Basebolen', 'Julian'].includes(currentUser?.username);
    const showBatterSim = canViewDice && card && card.type === 'Batter';
    const showPitcherSim = canViewDice && card && card.type !== 'Batter';

    useEffect(() => {
        if (!card || !card.year) return;

        const availableVariants = [`${card.year}-base`];
        if (cardInventory && cardInventory.Series) {
            cardInventory.Series.forEach(series => availableVariants.push(series.seriesKey));
        }
        setVariants(availableVariants);
    }, [cardInventory, card]);

    useEffect(() => {
        const loadData = async () => {
            setLoading(true);
            setError(null);
            try {
                if (inventoryID) {
                    const inventory = await fetchCardInventoryByID(inventoryID);
                    const fullCard = await fetchCardDetails(inventory.Card.cardID);
                    setCardInventory(inventory);
                    setCard(fullCard);
                } else if (cardID) {
                    const [cardData, inventoryData] = await Promise.all([
                        card ? Promise.resolve(card) : fetchCardDetails(cardID),
                        fetchCardInventory(cardID),
                    ]);
                    if (!card) setCard(cardData);
                    setCardInventory(inventoryData);
                }
            } catch (err) {
                setError('Error fetching card details');
                console.error('Error fetching card details:', err);
            } finally {
                setLoading(false);
            }
        };
        loadData();
    }, [cardID, inventoryID]);

    useEffect(() => {
        if (!showPitcherSim || !card?.year) return;
        fetchAverageBatter(card.year).then(setAverageBatter).catch(() => setAverageBatter(null));
    }, [showPitcherSim, card?.year]);

    const handleRollDice = useCallback(() => {
        if (!card) return;
        if (showPitcherSim && averageBatter) {
            const { results } = rollDiceForCardVs(averageBatter, card, numRolls);
            setSimStats(calculateBattingStats(results));
            setPitcherSimStats(calcPitchingSimStats(results));
        } else {
            const results = rollDiceForCard(card, numRolls);
            setSimStats(calculateBattingStats(results));
        }
    }, [card, numRolls, showPitcherSim, averageBatter]);

    const calcBattingStats = batting => {
        const singles = batting.H - (batting['2B'] + batting['3B'] + batting.HR);
        const average = formatAverage(batting.H / batting.AB);
        const obp = formatAverage(
            (batting.H + batting.BB + batting.HBP) / (batting.AB + batting.BB + batting.HBP + batting.SF)
        );
        const slugging = formatAverage((singles + batting['2B'] * 2 + batting['3B'] * 3 + batting.HR * 4) / batting.AB);
        const ops = formatAverage(parseFloat(obp) + parseFloat(slugging));
        return { average, obp, slugging, ops };
    };

    const calculatePitchingStats = pitching => {
        const era = ((pitching.ER * 9) / (pitching.IPouts / 3)).toFixed(2);
        const k9 = ((pitching.SO / (pitching.IPouts / 3)) * 9).toFixed(2);
        const hr9 = ((pitching.HR / (pitching.IPouts / 3)) * 9).toFixed(2);
        const whip = ((pitching.BB + pitching.H) / (pitching.IPouts / 3)).toFixed(2);
        const bb9 = ((pitching.BB / (pitching.IPouts / 3)) * 9).toFixed(2);
        const h9 = ((pitching.H / (pitching.IPouts / 3)) * 9).toFixed(2);
        return { era, k9, hr9, bb9, h9, whip };
    };

    const calculateInventoryBattingStats = inventory => {
        const hits = (inventory.h1 || 0) + (inventory.h2 || 0) + (inventory.h3 || 0) + (inventory.hr || 0);
        const singles = inventory.h1;
        const average = inventory.ab ? formatAverage((hits || 0) / (inventory.ab || 0)) : 0;
        const obp = inventory.ab ? formatAverage((hits + inventory.bb) / (inventory.ab + inventory.bb)) : 0;
        const slugging = inventory.ab
            ? formatAverage((singles + inventory.h2 * 2 + inventory.h3 * 3 + inventory.hr * 4) / inventory.ab)
            : 0;
        const ops = formatAverage(parseFloat(obp) + parseFloat(slugging));
        return { average, obp, slugging, ops };
    };

    const calculateInventoryPitchingStats = inventory => {
        const k9 = inventory.ipOuts ? ((inventory.so / (inventory.ipOuts / 3)) * 9).toFixed(2) : 0;
        const bb9 = inventory.ipOuts ? ((inventory.bb / (inventory.ipOuts / 3)) * 9).toFixed(2) : 0;
        const h9 = inventory.ipOuts
            ? (((inventory.h1 + inventory.h2 + inventory.h3 + inventory.hr) / (inventory.ipOuts / 3)) * 9).toFixed(2)
            : 0;
        const hr9 = inventory.ipOuts ? ((inventory.hr / (inventory.ipOuts / 3)) * 9).toFixed(2) : 0;
        const whip = inventory.ipOuts
            ? (
                  (inventory.bb + inventory.h1 + inventory.h2 + inventory.h3 + inventory.hr) /
                  (inventory.ipOuts / 3)
              ).toFixed(2)
            : 0;
        const era = inventory.ipOuts ? ((inventory.runs || 0) / (inventory.ipOuts / 27)).toFixed(2) : 0;
        return { h9, bb9, hr9, k9, whip, era };
    };

    if (loading) {
        return <LoadingSpinner />;
    }

    if (error) {
        return <p className="error">{error}</p>;
    }

    const hasInventory = !!cardInventory && !Array.isArray(cardInventory);
    const isBatter = card.type === 'Batter';

    const combinedBatting = card.Battings ? combineBattingRecords(card.Battings) : null;
    const combinedPitching = card.Pitchings ? combinePitchingRecords(card.Pitchings) : null;
    const battingStats = combinedBatting ? calcBattingStats(combinedBatting) : null;
    const pitchingStats = combinedPitching ? calculatePitchingStats(combinedPitching) : null;
    const invBattingStats = hasInventory && isBatter ? calculateInventoryBattingStats(cardInventory) : null;
    const invPitchingStats = hasInventory && !isBatter ? calculateInventoryPitchingStats(cardInventory) : null;

    const upgrades = parseCardUpgrades(hasInventory ? cardInventory.upgrades : null);
    const upgradeLevel = getUpgradeLevel(upgrades, getUpgradeOptionsForCardType(isBatter));
    const { effectiveBatterSpeed } =
        getEffectiveCardStatsFromUpgrades({ card, upgrades, globals: GLOBALS });
    const speedDelta =
        isBatter && card.speed != null && effectiveBatterSpeed != null
            ? effectiveBatterSpeed - card.speed
            : null;

    const invHits = hasInventory ? (cardInventory.h1 || 0) + (cardInventory.h2 || 0) + (cardInventory.h3 || 0) + (cardInventory.hr || 0) : null;
    const showSim = showBatterSim || showPitcherSim;
    const colSpan = showSim ? 4 : 3;

    return (
        <div className="card-detail-container">
            <BackButton to="/cards/user" />
            <div className="card-detail-page">
                <div className="card-detail-left">
                    <Card card={{ ...card, CardInventory: hasInventory ? cardInventory : null }} variation={cardInventory?.CardVariation || null} showLabel />
                    <div className="card-detail-info">
                        <div className="card-detail-meta">
                            <span>{teamNameTranslator(card.teamNameID)} / {card.year}</span>
                            {isBatter && (
                                <span>
                                    {' / '}Speed: {card.speed}
                                    {typeof speedDelta === 'number' && speedDelta > 0 && (
                                        <span className="card-detail-upgrade-delta"> (+{speedDelta})</span>
                                    )}
                                </span>
                            )}
                            {isBatter && combinedBatting && combinedBatting.AB <= GLOBALS.LOW_AB && (
                                <span className="card-detail-warning">* Low AB</span>
                            )}
                            {!isBatter && combinedPitching && combinedPitching.IPouts <= GLOBALS.LOW_IPOUTS && (
                                <span className="card-detail-warning">* Low IP</span>
                            )}
                        </div>
                        {hasInventory && (
                            <CardUpgrades card={card} cardInventory={cardInventory} onCardInventoryUpdated={setCardInventory} />
                        )}
                    </div>
                </div>

                <div className="card-detail-right">
                    <div className="table-scroll-wrapper">
                    <table className="card-detail-stats-table">
                        <thead>
                            <tr>
                                <th></th>
                                <th>{card.year} Real</th>
                                <th>Your Card</th>
                                {showSim && <th>Dice Sim</th>}
                            </tr>
                        </thead>
                        <tbody>
                            {showSim && (
                                <tr className="dice-sim-controls">
                                    <td></td>
                                    <td></td>
                                    <td></td>
                                    <td>
                                        <div className="dice-sim-row">
                                            <input
                                                type="number"
                                                value={numRolls}
                                                onChange={e => setNumRolls(parseInt(e.target.value) || 1)}
                                                min="1"
                                                className="dice-sim-input"
                                            />
                                            <button onClick={handleRollDice} className="dice-sim-button">Roll</button>
                                        </div>
                                    </td>
                                </tr>
                            )}
                            {isBatter && combinedBatting ? (
                                <>
                                    <StatSection title="Rate Stats" colSpan={colSpan}>
                                        <StatRow label="AVG" real={battingStats.average} game={invBattingStats?.average} sim={simStats ? formatAverage(parseFloat(simStats.average)) : null} showSim={showBatterSim || showPitcherSim} highlight />
                                        <StatRow label="OBP" real={battingStats.obp} game={invBattingStats?.obp} sim={simStats ? formatAverage(parseFloat(simStats.obp)) : null} showSim={showBatterSim || showPitcherSim} />
                                        <StatRow label="SLG" real={battingStats.slugging} game={invBattingStats?.slugging} sim={simStats ? formatAverage(parseFloat(simStats.slugging)) : null} showSim={showBatterSim || showPitcherSim} />
                                        <StatRow label="OPS" real={battingStats.ops} game={invBattingStats?.ops} sim={simStats ? formatAverage(parseFloat(simStats.ops)) : null} showSim={showBatterSim || showPitcherSim} highlight />
                                    </StatSection>
                                    <StatSection title="Counting Stats" colSpan={colSpan}>
                                        <StatRow label="G" real={combinedBatting.G} game={null} sim={null} showSim={showBatterSim || showPitcherSim} />
                                        <StatRow label="AB" real={combinedBatting.AB} game={hasInventory ? cardInventory.ab : null} sim={simStats?.ab} showSim={showBatterSim || showPitcherSim} highlight />
                                        <StatRow label="H" real={combinedBatting.H} game={invHits} sim={simStats?.h} showSim={showBatterSim || showPitcherSim} />
                                        <StatRow label="1B" real={combinedBatting.H - combinedBatting['2B'] - combinedBatting['3B'] - combinedBatting.HR} game={hasInventory ? cardInventory.h1 : null} sim={simStats?.h1} showSim={showBatterSim || showPitcherSim} />
                                        <StatRow label="2B" real={combinedBatting['2B']} game={hasInventory ? cardInventory.h2 : null} sim={simStats?.h2} showSim={showBatterSim || showPitcherSim} />
                                        <StatRow label="3B" real={combinedBatting['3B']} game={hasInventory ? cardInventory.h3 : null} sim={simStats?.h3} showSim={showBatterSim || showPitcherSim} />
                                        <StatRow label="HR" real={combinedBatting.HR} game={hasInventory ? cardInventory.hr : null} sim={simStats?.hr} showSim={showBatterSim || showPitcherSim} highlight />
                                        <StatRow label="R" real={combinedBatting.R} game={hasInventory ? cardInventory.runs : null} sim={null} showSim={showBatterSim || showPitcherSim} />
                                        <StatRow label="RBI" real={combinedBatting.RBI} game={hasInventory ? cardInventory.rbi : null} sim={null} showSim={showBatterSim || showPitcherSim} />
                                        <StatRow label="BB" real={combinedBatting.BB} game={hasInventory ? cardInventory.bb : null} sim={simStats?.bb} showSim={showBatterSim || showPitcherSim} />
                                        <StatRow label="SO" real={combinedBatting.SO} game={hasInventory ? cardInventory.so : null} sim={simStats?.so} showSim={showBatterSim || showPitcherSim} />
                                        <StatRow label="SB" real={combinedBatting.SB} game={hasInventory ? cardInventory.sb : null} sim={null} showSim={showBatterSim || showPitcherSim} />
                                        <StatRow label="CS" real={combinedBatting.CS} game={hasInventory ? cardInventory.cs : null} sim={null} showSim={showBatterSim || showPitcherSim} />
                                        <StatRow label="SH" real={combinedBatting.SH} game={hasInventory ? cardInventory.sh : null} sim={null} showSim={showBatterSim || showPitcherSim} />
                                        <StatRow label="GIDP" real={combinedBatting.GIDP} game={hasInventory ? cardInventory.gidp : null} sim={null} showSim={showBatterSim || showPitcherSim} />
                                    </StatSection>
                                </>
                            ) : !isBatter && combinedPitching ? (
                                <>
                                    {showPitcherSim && averageBatter && (
                                        <tr className="pitcher-sim-label">
                                            <td colSpan={colSpan}>
                                                <small>Sim vs: <a href="#" onClick={e => { e.preventDefault(); setShowBatterPreview(true); }}>{averageBatter.name}</a> ({averageBatter.year}, rating {averageBatter.rating})</small>
                                            </td>
                                        </tr>
                                    )}
                                    {showBatterPreview && averageBatter && (
                                        <CardPreviewModal card={averageBatter} onClose={() => setShowBatterPreview(false)} />
                                    )}
                                    <StatSection title="Sim Batting Allowed" colSpan={colSpan}>
                                        <StatRow label="AVG" real={null} game={null} sim={simStats ? formatAverage(parseFloat(simStats.average)) : null} showSim={showPitcherSim} highlight />
                                        <StatRow label="OBP" real={null} game={null} sim={simStats ? formatAverage(parseFloat(simStats.obp)) : null} showSim={showPitcherSim} />
                                        <StatRow label="SLG" real={null} game={null} sim={simStats ? formatAverage(parseFloat(simStats.slugging)) : null} showSim={showPitcherSim} />
                                        <StatRow label="OPS" real={null} game={null} sim={simStats ? formatAverage(parseFloat(simStats.ops)) : null} showSim={showPitcherSim} highlight />
                                    </StatSection>
                                    <StatSection title="Rate Stats" colSpan={colSpan}>
                                        <StatRow label="ERA" real={pitchingStats.era} game={invPitchingStats?.era} showSim={false} highlight />
                                        <StatRow label="WHIP" real={pitchingStats.whip} game={invPitchingStats?.whip} sim={pitcherSimStats?.whip} showSim={showPitcherSim} />
                                        <StatRow label="K/9" real={pitchingStats.k9} game={invPitchingStats?.k9} sim={pitcherSimStats?.k9} showSim={showPitcherSim} />
                                        <StatRow label="BB/9" real={pitchingStats.bb9} game={invPitchingStats?.bb9} sim={pitcherSimStats?.bb9} showSim={showPitcherSim} />
                                        <StatRow label="H/9" real={pitchingStats.h9} game={invPitchingStats?.h9} sim={pitcherSimStats?.h9} showSim={showPitcherSim} />
                                        <StatRow label="HR/9" real={pitchingStats.hr9} game={invPitchingStats?.hr9} sim={pitcherSimStats?.hr9} showSim={showPitcherSim} />
                                    </StatSection>
                                    <StatSection title="Counting Stats" colSpan={colSpan}>
                                        <StatRow label="G" real={combinedPitching.G} game={null} showSim={false} />
                                        <StatRow label="GS" real={combinedPitching.GS} game={null} showSim={false} />
                                        <StatRow label="W" real={combinedPitching.W} game={null} showSim={false} />
                                        <StatRow label="L" real={combinedPitching.L} game={null} showSim={false} />
                                        <StatRow label="CG" real={combinedPitching.CG} game={null} showSim={false} />
                                        <StatRow label="SHO" real={combinedPitching.SHO} game={null} showSim={false} />
                                        <StatRow label="SV" real={combinedPitching.SV} game={null} showSim={false} />
                                        <StatRow label="IP" real={(combinedPitching.IPouts / 3).toFixed(1)} game={hasInventory ? (cardInventory.ipOuts / 3).toFixed(1) : null} sim={pitcherSimStats?.ip} showSim={showPitcherSim} highlight />
                                        <StatRow label="H" real={combinedPitching.H} game={invHits} sim={pitcherSimStats?.hits} showSim={showPitcherSim} />
                                        <StatRow label="1B" real={null} game={hasInventory ? cardInventory.h1 : null} showSim={false} />
                                        <StatRow label="2B" real={null} game={hasInventory ? cardInventory.h2 : null} showSim={false} />
                                        <StatRow label="3B" real={null} game={hasInventory ? cardInventory.h3 : null} showSim={false} />
                                        <StatRow label="HR" real={combinedPitching.HR} game={hasInventory ? cardInventory.hr : null} sim={pitcherSimStats?.homeRuns} showSim={showPitcherSim} />
                                        <StatRow label="ER" real={combinedPitching.ER} game={hasInventory ? cardInventory.runs : null} sim={pitcherSimStats?.runs} showSim={showPitcherSim} />
                                        <StatRow label="BB" real={combinedPitching.BB} game={hasInventory ? cardInventory.bb : null} sim={pitcherSimStats?.walks} showSim={showPitcherSim} />
                                        <StatRow label="SO" real={combinedPitching.SO} game={hasInventory ? cardInventory.so : null} sim={pitcherSimStats?.strikeouts} showSim={showPitcherSim} highlight />
                                    </StatSection>
                                </>
                            ) : (
                                <tr><td colSpan={colSpan}>No stats available for this card.</td></tr>
                            )}
                        </tbody>
                    </table>
                    </div>
                </div>
            </div>

            <CardBacksPicker
                card={card}
                cardInventory={hasInventory ? cardInventory : null}
                variants={variants}
                onCardInventoryUpdated={setCardInventory}
            />
        </div>
    );
};

export default CardDetailsPage;
